import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ToastGlobalService } from 'src/app/layout/toast-global/toast-global.service';
import { VehicleService } from '../vehicle/service/vehicle.service';
import { ScheduleService } from '../schedule/schedule.service';
import { ScheduleAttribute, ScheduleAttributeGroup } from 'src/app/models/scheduleAttribute';
import { Vehicle } from '../../models/vehicle';
import { TranslateService } from '@ngx-translate/core';
import { VehicleState } from 'src/app/models/vehicleState';
import { VehicleDetailService } from '../vehicle-detail/vehicle-detail.service';
import { VehicleFleetSchedule } from 'src/app/models/vehicleFleetSchedule';
import { KeyValueExtra } from 'src/app/models/keyValueExtra';
import { ListDefinition } from 'src/app/models/listDefinition';
import { AttributeListService } from 'src/app/shared/attribute-list/attribute-list.service';
import { ScheduleAttributeValue } from '../../models/scheduleAttribute';
import { ListDefinitionService } from '../../shared/services/listDefinition.service';
import { Observable, Subject, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { AuthorizationService } from 'src/app/services/authorization.service';
import { AuthService } from 'src/app/services/auth.service';
import { MaintainVehicle } from 'src/app/models/constant/right';
import { jsonCompare } from 'src/app/shared/handler/common-functions.util';
import { Select } from '@ngxs/store';
import { TenantSessionState } from 'src/app/store/state/tenant-session.state';
import { KeyValue } from '@angular/common';

@Component({
    selector: 'app-vehicle-config',
    templateUrl: './vehicle-config.component.html',
    styleUrls: ['./vehicle-config.component.css'],
    standalone: false
})
export class VehicleConfigComponent implements OnInit, OnDestroy {
  canDeactivate(): Observable<boolean> | boolean {
    let isSchedulingAttributesChanged = false;
    this.vfs?.forEach(fs => {
      fs.displayAttributes?.forEach(sa => {
        if (fs.schedulingAttributes[sa.key] !== sa.value) {
          isSchedulingAttributesChanged = true;
        }
      });
    });
    return !isSchedulingAttributesChanged
      && jsonCompare(this.vehicle.attributes, this.attributes);
  }
  @Select(TenantSessionState.getPartTypes) partTypes$: Observable<KeyValue<number, string>[]>;
  @Input() vehicle: Vehicle;
  @Input() vehicleState: VehicleState;
  @Input() tenantId: string;
  @Output() public functionCallback = new EventEmitter();
  public PartTypeValues: KeyValue<number, string>[];
  public buildConfiguration: any = {};
  public vfs: VehicleFleetSchedule[] = [];
  private destroy$ = new Subject();
  oldVehicle: Vehicle; // Used to detect changes
  attributes: KeyValueExtra[] = [];
  listDefinitions: ListDefinition[];
  scheduleAttributeOptions: any;
  canEdit: boolean;
  hasLiftGateBattery: boolean = false;
  hasABS: boolean = false;
  hasATIS: boolean = false;
  constructor(
    private vehicleService: VehicleService,
    private vehicleDetailService: VehicleDetailService,
    private scheduleService: ScheduleService,
    private toastGlobalService: ToastGlobalService,
    private translate: TranslateService,
    private attributeListService: AttributeListService,
    private listService: ListDefinitionService,
    private authorizationService: AuthorizationService,
    private authService: AuthService) { }

  ngOnInit(): void {
    this.partTypes$.pipe(takeUntil(this.destroy$)).subscribe((partTypes) => {
      this.PartTypeValues = partTypes?.filter(p => p.value !== 'Null' && p.value !== 'Unknown') ?? [];
    });
    this.canEdit = this.authorizationService.hasRight(
      this.authService.user.profile,
      MaintainVehicle
    );
    this.oldVehicle = { ...this.vehicle };
    this.buildConfiguration = this.vehicle.buildConfiguration;
    this.attributes = this.vehicle.attributes;
    this.updateAttributeValue();
    if (this.vehicle.vehicleFleetSchedules != null && this.vehicle.vehicleFleetSchedules.length > 0) {
      this.vfs = this.vehicle.vehicleFleetSchedules;
      this.listService.getListServiceByArea('scheduleCriteria').subscribe(res => {
        if (res && res.length > 0) {
          this.scheduleAttributeOptions = res[0].data;
          this.vfs.forEach(fs => {
            fs.displayAttributes = [];
            Object.keys(fs.schedulingAttributes).forEach(key => {
              let values = this.scheduleAttributeOptions
                .filter(x => {
                  return x.key.toLowerCase() === key.toLowerCase();
                })
                .map(y => {
                  return y.value
                });
              let newAttribute = { key: key, value: fs.schedulingAttributes[key], values: values };
              fs.displayAttributes.push(newAttribute);
            });
          });
        }
      });


    }
    // Get data from db with a ListDefinition service
    this.attributeListService.getAreaLists('vehicle')
      .subscribe(data => {
        if (data.length > 0) {
          this.listDefinitions = data;
        }
      });
  }
  updateAttributeValue(): void {
    this.hasABS = this.getAttrBool('HasABS');
    this.hasLiftGateBattery = this.getAttrBool('HasLiftGateBattery');
    this.hasATIS = this.getAttrBool('HasATIS');
  }
  updateVehicleConfig(): void {
    this.vehicle.buildConfiguration = this.buildConfiguration;
    this.vehicle.attributes = this.attributes;
    this.updateAttributeValue();
    let isSchedulingAttributesChanged = false;
    this.vfs?.forEach(fs => {
      fs.displayAttributes?.forEach(sa => {
        if (fs.schedulingAttributes[sa.key] !== sa.value) {
          isSchedulingAttributesChanged = true;
        }
        fs.schedulingAttributes[sa.key] = sa.value;
      });
    });

    if (isSchedulingAttributesChanged) {
      const message = this.translate.instant('Do you want to update schedules with new attributes?');
      isSchedulingAttributesChanged = confirm(message);
    }
    
    this.vehicleService.updateVehicle(this.vehicle).pipe(
      switchMap(() => {
        if (isSchedulingAttributesChanged) {
          return this.scheduleService.updateVehicleSchedules(this.vehicle.vin, this.vfs)
        }
        else {
          return of({});
        }
      })).subscribe(() => {
        const toast = this.translate.instant('@updateSuccessful');
        this.toastGlobalService.show(toast, { classname: 'success' });
        const vehicleUpdatedEvent = {
          action: 'vehicleUpdated'
        };
        this.functionCallback.emit(vehicleUpdatedEvent);

        if (isSchedulingAttributesChanged) {
          const newEvent = {
            action: 'refreshSchedules'
          };
          this.functionCallback.emit(newEvent);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
  
  filterOptions($event: any, data: any) {
    let values = this.scheduleAttributeOptions
      .filter(x => {
        return x.key.toLowerCase() === data.key.toLowerCase()
          && x.value.toLowerCase().includes(data.value.toLowerCase());
      })
      .map(y => {
        return y.value
      });
    data.values = values;
  }

  getAttrBool(key: string): boolean {
    return this.getAttr(key)?.toLowerCase() === 'true';
  }

  getAttr(key: string): string {
    var attrMatches: KeyValueExtra[] = this.attributes.filter(kve => kve.key == key);

    return attrMatches[0]?.value ?? '';
  }

  setAttr(key: string, val: string): void {
    var attrMatches: KeyValueExtra[] = this.attributes.filter(kve => kve.key == key);
    if (attrMatches.length == 0) {
      this.attributes.push(new KeyValueExtra(key, val));
    }
    else {
      attrMatches[0].value = val;
    }

    // This forces an update
    this.attributes = [...this.attributes];
  }

  toggleAttr(key: string) {
    if (this.getAttrBool(key)) {
      this.setAttr(key, 'False');
    }
    else {
      this.setAttr(key, 'True');
    }
    this.updateAttributeValue();
  }
}
